import cx from 'clsx';
import React, { useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ViewDetailsIcon } from '@assets/svg/executive-summary/ic-view-details.svg';
import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import {
  selectAssetHealthCauseCategorySorting,
  selectAssetHealthEquipmentSorting,
  selectAssetHealthUrgency,
  useAssetHealthOverviewStoreActions,
} from '@pages/ExecutiveSummary/store/useAssetHealthOverviewFilterStorePersist';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useAssetHealthCauseCategoryListQuery } from '../../services/useAssetHealthCauseCategoryListQuery';
import CauseCategoryTableSkeleton from './CauseCategoryTableSkeleton';
import style from './style.module.scss';

const CauseCategoryTable = () => {
  const { t } = useTranslation();

  const causeCategoryTableContainerRef = useRef(null);

  const { customerId } = useParams();
  const { pathname } = useLocation();

  const { setAssetHealthCauseCategorySorting } = useAssetHealthOverviewStoreActions();
  const locationSelectValue = selectAppLocationSelectValue();
  const sorting = selectAssetHealthCauseCategorySorting();
  const urgency = selectAssetHealthUrgency();

  const { data, isLoading } = useAssetHealthCauseCategoryListQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    urgency: urgency.value,
  });

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setAssetHealthCauseCategorySorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setAssetHealthCauseCategorySorting(initialSorting);
    } else {
      setAssetHealthCauseCategorySorting({ id, desc: true });
    }
  };

  return (
    <>
      <div className={style.causeCategoryTableWrapper} ref={causeCategoryTableContainerRef}>
        <table className={style.causeCategoryTable}>
          <thead>
            <tr>
              <th>
                <div className={style.faultNameColumn}>
                  <div>
                    <div className={style.faultNameColumnTitle}>{t('Failure Cause Categories')}</div>
                    <div className={style.ellipsisTextOneLine}>({t('Active Action Items')})</div>
                  </div>
                  <TableSort
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'fault_name' })}
                    onClick={() => onChangeSorting('fault_name')}
                    data-tooltip-content="fault_name"
                    data-tooltip-id="cause-sort-tooltip"
                  />
                </div>
              </th>
              <th>
                {t('Total')}
                <TableSort
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'action_items_count' })}
                  onClick={() => onChangeSorting('action_items_count')}
                  data-tooltip-content="action_items_count"
                  data-tooltip-id="cause-sort-tooltip"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <CauseCategoryTableSkeleton />
            ) : data!.length ? (
              data!.map((row) => (
                <tr key={row.fault_name} className={style.bodyRow}>
                  <td>
                    <div
                      className={style.ellipsisTextTwoLine}
                      data-tooltip-content={row.fault_name}
                      data-tooltip-id="cause-cell-tooltip"
                    >
                      {row.fault_name}
                    </div>
                  </td>
                  <td>{row.action_items_count}</td>
                </tr>
              ))
            ) : (
              <TableEmptyRow text={t('No cause categories were found.')} />
            )}
          </tbody>
        </table>
        <TooltipSort id="cause-sort-tooltip" sorting={sorting} />
        <Tooltip id="cause-cell-tooltip" place="top-start" />
      </div>
      <div className={style.viewDetailsLink}>
        <Link
          to={`/customer/${customerId}/executive-summary/failure-cause-categories`}
          state={{ prevPage: pathname }}
        >
          {t('View details')}
          <ViewDetailsIcon className={style.viewDetailsIcon} id="view-action-item" />
        </Link>
      </div>
      <div className={style.tableFooter}>
        <ButtonGoUp ref={causeCategoryTableContainerRef} />
      </div>
    </>
  );
};

export default CauseCategoryTable;
